import { useMemo, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { groupBy } from 'lib/array/arrayUtils'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Divider from 'LuxKit/Divider'
import CartPanelHeader from './CartPanelHeader'
import CartPanelSection from './CartPanelSection'
import CartPanelFooter from './CartPanelFooter'
import CartPanelEmptyState from './CartPanelEmptyState'
import { isCartPanelItemCheckoutReady } from '../utils/cartPanelUtils'
import type { CartPanelFlightItem, CartPanelItem } from '../hooks/useCartPanelItems'

const SIDEBAR_BREAKPOINT = 850
const DESKTOP_PANEL_PADDING = 24
const SIDEBAR_WIDTH = 360

const PanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  container-type: inline-size;
`

const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @container (min-width: ${rem(SIDEBAR_BREAKPOINT)}) {
    flex-direction: row;
  }
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: ${rem(24)};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  @container (min-width: ${rem(SIDEBAR_BREAKPOINT)}) {
    padding: ${rem(DESKTOP_PANEL_PADDING)};
  }
`

const SidebarWrapper = styled.div`
  display: none;

  @container (min-width: ${rem(SIDEBAR_BREAKPOINT)}) {
    display: flex;
    flex-direction: column;
    width: ${rem(SIDEBAR_WIDTH)};
    padding-block: ${rem(DESKTOP_PANEL_PADDING)};
    padding-inline: 0 ${rem(DESKTOP_PANEL_PADDING)};
    flex-shrink: 0;
    overflow-y: auto;
  }
`

const StickyFooterWrapper = styled.div`
  @container (min-width: ${rem(SIDEBAR_BREAKPOINT)}) {
    display: none;
  }
`

interface Props {
  items: Array<CartPanelItem>
  isLoading: boolean
  totalPrice: number
  totalValue: number
  totalItemCount: number
  hasCheckoutReadyItems: boolean
  showReadyItemsOnlyNote: boolean
  loyaltyCalculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest>
  isLoggedIn: boolean
  isStreaming: boolean
  onClose: () => void
  onToggleInclude: (item: CartPanelItem) => void
  onSetDates: () => void
  onChooseTickets: (item: CartPanelItem) => void
  onChooseDate: (item: CartPanelItem) => void
  onChooseTime: (item: CartPanelItem) => void
  onChooseDifferentTickets: (item: CartPanelItem) => void
  onChooseFlight: (item: CartPanelFlightItem) => void
  onRefreshFlight: (item: CartPanelFlightItem) => void
  onChangeFlight: (item: CartPanelFlightItem) => void
  onViewFlightDetails: (item: CartPanelFlightItem) => void
  refreshingFlightKey: string | undefined
  onDelete: (item: CartPanelItem) => void
  onCheckout: () => Promise<void>
  isCheckoutLoading: boolean
  flightSearchDrawer: ReactNode
  flightDetailsDrawer: ReactNode
}

function isCartPanelCheckoutBlocked(item: CartPanelItem): boolean {
  return !item.cartExcluded && !isCartPanelItemCheckoutReady(item)
}

function CartPanel({
  items,
  isLoading,
  totalPrice,
  totalValue,
  totalItemCount,
  hasCheckoutReadyItems,
  showReadyItemsOnlyNote,
  loyaltyCalculationRequests,
  isLoggedIn,
  isStreaming,
  onClose,
  onToggleInclude,
  onSetDates,
  onChooseTickets,
  onChooseDate,
  onChooseTime,
  onChooseDifferentTickets,
  onChooseFlight,
  onRefreshFlight,
  onChangeFlight,
  onViewFlightDetails,
  refreshingFlightKey,
  onDelete,
  onCheckout,
  isCheckoutLoading,
  flightSearchDrawer,
  flightDetailsDrawer,
}: Props) {
  const intl = useIntl()

  const groupedItems = useMemo(
    () => groupBy(items, item => item.productType),
    [items],
  )

  const accommodationItems = groupedItems.get('accommodation') ?? []
  const experienceItems = groupedItems.get('experience') ?? []
  const flightItems = groupedItems.get('flight') ?? []
  const checkoutBlockerItemCount = useMemo(
    () => items.filter(isCartPanelCheckoutBlocked).length,
    [items],
  )
  const accommodationTitle = intl.formatMessage({
    id: '5wptrCN0E-MZdkCRRur2',
    defaultMessage: 'Accommodation',
    description: 'Section title for accommodation items in the trip planner cart panel',
  })

  const experiencesTitle = intl.formatMessage({
    id: 'ex4Hhv75OUDizjt1F064',
    defaultMessage: 'Experiences',
    description: 'Section title for experience items in the trip planner cart panel',
  })

  const flightsTitle = intl.formatMessage({
    id: 'ngcCNUBlYflpWDnKtLAl',
    defaultMessage: 'Flights',
    description: 'Section title for flight items in the trip planner cart panel',
  })

  const hasItems = items.length > 0
  const showGlobalLoading = isLoading && !hasItems

  const footer = (
    <CartPanelFooter
      totalPrice={totalPrice}
      totalValue={totalValue}
      totalItemCount={totalItemCount}
      hasCheckoutReadyItems={hasCheckoutReadyItems}
      showReadyItemsOnlyNote={showReadyItemsOnlyNote}
      checkoutBlockerItemCount={checkoutBlockerItemCount}
      loyaltyCalculationRequests={loyaltyCalculationRequests}
      isLoading={isLoading}
      isCheckoutLoading={isCheckoutLoading}
      isLoggedIn={isLoggedIn}
      onCheckout={onCheckout}
    />
  )

  return (
    <PanelWrapper>
      <CartPanelHeader onClose={onClose} />
      <Divider kind="primary" />
      <PanelBody>
        <ScrollableContent>
          <LoadingIndicator visible={showGlobalLoading} floating opaque />
          {hasItems && accommodationItems.length > 0 && <CartPanelSection
            title={accommodationTitle}
            icon={<LineBedIcon />}
            items={accommodationItems}
            onToggleInclude={onToggleInclude}
            onSetDates={onSetDates}
            onChooseTickets={onChooseTickets}
            onChooseDate={onChooseDate}
            onChooseTime={onChooseTime}
            onChooseDifferentTickets={onChooseDifferentTickets}
            onChooseFlight={onChooseFlight}
            onRefreshFlight={onRefreshFlight}
            onChangeFlight={onChangeFlight}
            onViewFlightDetails={onViewFlightDetails}
            refreshingFlightKey={refreshingFlightKey}
            onDelete={onDelete}
            isStreaming={isStreaming}
          />}
          {hasItems && experienceItems.length > 0 && <CartPanelSection
            title={experiencesTitle}
            icon={<LineHotAirBalloonIcon />}
            items={experienceItems}
            onToggleInclude={onToggleInclude}
            onSetDates={onSetDates}
            onChooseTickets={onChooseTickets}
            onChooseDate={onChooseDate}
            onChooseTime={onChooseTime}
            onChooseDifferentTickets={onChooseDifferentTickets}
            onChooseFlight={onChooseFlight}
            onRefreshFlight={onRefreshFlight}
            onChangeFlight={onChangeFlight}
            onViewFlightDetails={onViewFlightDetails}
            refreshingFlightKey={refreshingFlightKey}
            onDelete={onDelete}
            isStreaming={isStreaming}
          />}
          {hasItems && flightItems.length > 0 && <CartPanelSection
            title={flightsTitle}
            icon={<LinePlaneDepartureIcon />}
            items={flightItems}
            onToggleInclude={onToggleInclude}
            onSetDates={onSetDates}
            onChooseTickets={onChooseTickets}
            onChooseDate={onChooseDate}
            onChooseTime={onChooseTime}
            onChooseDifferentTickets={onChooseDifferentTickets}
            onChooseFlight={onChooseFlight}
            onRefreshFlight={onRefreshFlight}
            onChangeFlight={onChangeFlight}
            onViewFlightDetails={onViewFlightDetails}
            refreshingFlightKey={refreshingFlightKey}
            onDelete={onDelete}
            isStreaming={isStreaming}
          />}
          {!hasItems && !isLoading && <CartPanelEmptyState />}
        </ScrollableContent>
        {hasItems && (
          <SidebarWrapper>
            {footer}
          </SidebarWrapper>
        )}
      </PanelBody>
      {hasItems && (
        <StickyFooterWrapper>
          <Divider kind="primary" />
          {footer}
        </StickyFooterWrapper>
      )}
      {flightSearchDrawer}
      {flightDetailsDrawer}
    </PanelWrapper>
  )
}

export default CartPanel
